import { useState, useEffect, useMemo } from 'react';
import { Loader2 } from 'lucide-react';

interface ProcessingViewProps {
  file: File;
  statusText?: string;
}

const STATUS_MESSAGES = [
  'Uploading video...',
  'Removing background...',
  'Processing frames...',
  'Almost done...',
];

export function ProcessingView({ file, statusText }: ProcessingViewProps) {
  const [messageIndex, setMessageIndex] = useState(0);
  const thumbnailUrl = useMemo(() => URL.createObjectURL(file), [file]);

  useEffect(() => {
    return () => URL.revokeObjectURL(thumbnailUrl);
  }, [thumbnailUrl]);

  // Cycle through status messages
  useEffect(() => {
    if (statusText) return; // Don't cycle if external status is provided
    const interval = setInterval(() => {
      setMessageIndex((prev) => (prev + 1) % STATUS_MESSAGES.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [statusText]);

  const displayText = statusText || STATUS_MESSAGES[messageIndex];

  return (
    <div className="flex flex-col items-center gap-6 py-12">
      {/* Video thumbnail with spinner overlay */}
      <div className="relative w-full max-w-md aspect-video rounded-xl overflow-hidden bg-muted">
        <video
          src={thumbnailUrl}
          className="w-full h-full object-cover"
          muted
        />
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="w-10 h-10 text-white animate-spin" />
          </div>
        </div>
      </div>

      {/* Status text */}
      <div className="text-center space-y-2">
        <p className="text-lg font-medium text-foreground animate-pulse">
          {displayText}
        </p>
        <p className="text-sm text-muted-foreground">
          This usually takes 30-90 seconds
        </p>
      </div>

      {/* Progress bar */}
      <div className="w-full max-w-xs">
        <div className="h-1.5 bg-muted rounded-full overflow-hidden">
          <div className="h-full bg-orange-500 rounded-full animate-[indeterminate_2s_ease-in-out_infinite]"
            style={{
              width: '40%',
              animation: 'indeterminate 2s ease-in-out infinite',
            }}
          />
        </div>
      </div>

      <style>{`
        @keyframes indeterminate {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(350%); }
        }
      `}</style>
    </div>
  );
}
